/**
 * JobPostForm - Formulário para publicar nova vaga de emprego
 * 
 * Extraído do App.js durante refatoração Fase 1
 * 
 * Funcionalidades:
 * - Criação de vaga de emprego
 * - Validação de limites do plano
 * - Campos: título, localização, salário, tipo, descrição, requisitos
 * 
 * Dependências:
 * - useTranslation (traduções)
 * - axios (requisições API)
 * - Card, Button, Input, Label, Textarea, Select (shadcn/ui)
 */

import React, { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useTranslation } from "../translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JobPostForm = ({ onUpdate }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    title: "", description: "", location: "", salary_range: "",
    requirements: "", job_type: "full-time"
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        alert(`❌ ${t('needLoginToPublish')}`);
        setLoading(false);
        return;
      }

      // Configure headers with token
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      // Check plan limits first
      const limitsResponse = await axios.get(`${API}/check-plan-limits/create_job`, config);
      
      if (!limitsResponse.data.allowed) {
        alert(`❌ ${limitsResponse.data.message}\n\n${t('considerUpgrade')}`);
        setLoading(false);
        return;
      }

      await axios.post(`${API}/jobs`, formData, config);
      setFormData({
        title: "", description: "", location: "", salary_range: "",
        requirements: "", job_type: "full-time"
      });
      onUpdate();
      alert(t('jobPublishedSuccess'));
    } catch (error) {
      console.error("Erro ao publicar vaga:", error);
      alert(`${t('errorPublishingJob')}: ` + (error.response?.data?.detail || error.message));
    }
    setLoading(false);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('publishNewJob')}</CardTitle>
        <CardDescription>{t('registerNewJobOpp')}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="title">{t('jobTitle')}</Label>
              <Input 
                id="title" 
                value={formData.title} 
                onChange={(e) => setFormData({...formData, title: e.target.value})} 
                required 
              />
            </div>
            <div>
              <Label htmlFor="location">{t('location')}</Label>
              <Input 
                id="location" 
                value={formData.location} 
                onChange={(e) => setFormData({...formData, location: e.target.value})} 
                required 
              />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="salary_range">{t('salaryRange')}</Label>
              <Input 
                id="salary_range" 
                value={formData.salary_range} 
                onChange={(e) => setFormData({...formData, salary_range: e.target.value})} 
                placeholder={t('salaryRangePlaceholder')}
              />
            </div>
            <div>
              <Label htmlFor="job_type">{t('jobType')}</Label>
              <Select value={formData.job_type} onValueChange={(value) => setFormData({...formData, job_type: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full-time">{t('fullTime')}</SelectItem>
                  <SelectItem value="part-time">{t('partTime')}</SelectItem>
                  <SelectItem value="contract">{t('contract')}</SelectItem>
                  <SelectItem value="seasonal">{t('seasonal')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="description">{t('jobDescription')}</Label>
            <Textarea 
              id="description" 
              value={formData.description} 
              onChange={(e) => setFormData({...formData, description: e.target.value})} 
              required 
              rows={4}
            />
          </div>
          <div>
            <Label htmlFor="requirements">{t('requirements')}</Label>
            <Textarea 
              id="requirements" 
              value={formData.requirements} 
              onChange={(e) => setFormData({...formData, requirements: e.target.value})} 
              required 
              rows={3}
            />
          </div>
          <Button type="submit" disabled={loading}>
            {loading ? t('publishing') : t('publishJob')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default JobPostForm;
